import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Youtube } from "lucide-react";
import { useLightbox } from "./VideoLightbox";

const STORAGE_KEY = "arsov-latest-drop-seen-v1";
const CHANNEL_URL = "https://www.youtube.com/channel/UCLpwX8aPs_ha-BC4bgQwVwg";

export default function LatestDropPopup({ video }) {
  const [open, setOpen] = useState(false);
  const { open: openLightbox } = useLightbox();

  useEffect(() => {
    if (!video) return;
    let seen = null;
    try { seen = localStorage.getItem(STORAGE_KEY); } catch (_) {}
    if (seen === video.video_id) return;
    const t = setTimeout(() => setOpen(true), 1800);
    return () => clearTimeout(t);
  }, [video]);

  const dismiss = () => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, video.video_id); } catch (_) {}
  };

  const watchNow = () => {
    dismiss();
    openLightbox(video);
  };

  if (!video) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.97 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          data-testid="latest-drop-popup"
          className="fixed bottom-5 right-5 left-5 sm:left-auto sm:bottom-6 sm:right-6 z-[60] w-auto sm:w-[380px] bg-[#0A0A0A] border border-white/10 shadow-[0_30px_80px_-20px_rgba(196,30,58,0.4)]"
        >
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            data-testid="latest-drop-dismiss"
            className="absolute top-3 right-3 z-10 w-8 h-8 bg-black/70 hover:bg-[#C41E3A] text-white flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          <div
            className="relative aspect-video bg-black overflow-hidden cursor-pointer group"
            onClick={watchNow}
            data-testid="latest-drop-thumb"
          >
            <img
              src={video.thumbnail_hq}
              onError={(e) => { e.currentTarget.src = video.thumbnail; }}
              alt={video.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-14 h-14 bg-[#C41E3A] flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play className="w-6 h-6 text-white fill-current" />
              </div>
            </div>
            <span className="absolute top-3 left-3 font-mono-tech text-[10px] uppercase tracking-[0.3em] bg-[#C41E3A] text-white px-2 py-1">
              ⏵ New Drop
            </span>
          </div>

          <div className="px-5 py-4">
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
              Just dropped on the channel
            </p>
            <h4 className="font-display text-lg uppercase leading-tight text-white line-clamp-2">
              {video.title}
            </h4>
            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={watchNow}
                data-testid="latest-drop-watch-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#C41E3A] hover:bg-white text-white hover:text-[#C41E3A] px-4 py-3 text-[10px] uppercase tracking-[0.25em] font-semibold transition-colors"
              >
                <Play className="w-3.5 h-3.5 fill-current" /> Watch Now
              </button>
              <a
                href={CHANNEL_URL}
                target="_blank"
                rel="noreferrer"
                onClick={dismiss}
                data-testid="latest-drop-subscribe-btn"
                className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white text-white/90 hover:text-white px-4 py-3 text-[10px] uppercase tracking-[0.25em] font-semibold transition-colors"
              >
                <Youtube className="w-3.5 h-3.5" /> Subscribe
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
